// What a file in Files shows as (spec "Files"): text and code, images, PDFs and CSVs preview in
// place; anything else only downloads. The bytes are the machine's, so they're never a page of
// this site: an image or a PDF becomes a Blob of a type picked here from a short list (never
// HTML or SVG, which could run script on this origin), text is shown as text.

import { read, type FileItem } from './files';

export type PreviewKind = 'text' | 'image' | 'pdf' | 'csv';

const IMAGES: Record<string, string> = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', avif: 'image/avif', bmp: 'image/bmp', ico: 'image/x-icon' };

const TEXT = new Set(
  (
    'txt md markdown mdx rst log json jsonl ndjson yaml yml toml ini cfg conf env lock xml html htm css scss less svg ' +
    'js mjs cjs jsx ts tsx mts cts go rs py rb php java kt kts swift m mm c h cc cpp hpp cs fs scala clj ex exs erl hs ' +
    'lua pl r jl dart zig nim sql graphql gql proto sh bash zsh fish ps1 bat vim el diff patch tf hcl gradle make cmake ' +
    'dockerfile gitignore gitattributes editorconfig npmrc nvmrc prettierrc eslintrc babelrc srt vtt tex bib'
  ).split(' '),
);
const TEXT_NAMES = new Set(['makefile', 'dockerfile', 'license', 'readme', 'procfile', 'gemfile', 'rakefile', 'justfile', 'caddyfile', 'brewfile', 'vagrantfile']);

/** How much of a file a preview reads: the rest is a download away. */
export const PREVIEW_MAX: Record<PreviewKind, number> = { text: 1 << 20, csv: 2 << 20, image: 25 << 20, pdf: 50 << 20 };

export const ext = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : name.startsWith('.') ? name.slice(1).toLowerCase() : '';
};

/** What the file previews as, from its name ("" for a file that only downloads). A file with no
 *  telling name is tried as text (readText says if it isn't). */
export function previewKind(item: Pick<FileItem, 'name' | 'kind'>): PreviewKind | '' {
  if (item.kind === 'dir') return '';
  const e = ext(item.name);
  if (IMAGES[e]) return 'image';
  if (e === 'pdf') return 'pdf';
  if (e === 'csv' || e === 'tsv') return 'csv';
  if (TEXT.has(e) || TEXT_NAMES.has(item.name.toLowerCase()) || !e) return 'text';
  return '';
}

export type Preview =
  | { kind: 'text'; text: string; cut: boolean }
  | { kind: 'csv'; rows: string[][]; cut: boolean }
  | { kind: 'image' | 'pdf'; url: string; cut: false }
  | { kind: 'binary' }
  | { kind: 'large' };

/** Reads the file for its preview. An image or a PDF comes back as an object URL the caller
 *  revokes; one larger than a preview reads is 'large' (download it instead). */
export async function loadPreview(item: FileItem, kind: PreviewKind): Promise<Preview> {
  const max = PREVIEW_MAX[kind];
  if ((kind === 'image' || kind === 'pdf') && item.size > max) return { kind: 'large' };
  const { bytes, cut } = await read(item.id, max);
  if (kind === 'image' || kind === 'pdf') {
    const type = kind === 'pdf' ? 'application/pdf' : IMAGES[ext(item.name)];
    return { kind, url: URL.createObjectURL(new Blob([bytes], { type })), cut: false };
  }
  const text = decode(bytes, cut);
  if (text === null) return { kind: 'binary' };
  if (kind === 'csv') return { kind, rows: parseCSV(text, ext(item.name) === 'tsv' ? '\t' : ','), cut };
  return { kind, text, cut };
}

/** The bytes as UTF-8 text, or null when they aren't text (a NUL, or not UTF-8). A cut can
 *  split a character at the end: that's let go. */
export function decode(bytes: ArrayBuffer, cut = false): string | null {
  const u8 = new Uint8Array(bytes);
  if (u8.subarray(0, 8192).includes(0)) return null;
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(cut ? trimPartial(u8) : u8);
  } catch {
    return null;
  }
}

/** The bytes without a UTF-8 character the cut split. */
function trimPartial(u8: Uint8Array) {
  let i = u8.length;
  while (i > 0 && i > u8.length - 4 && (u8[i - 1] & 0xc0) === 0x80) i--;
  if (i > 0 && u8[i - 1] >= 0xc0) return u8.subarray(0, i - 1);
  return u8;
}

/** The rows shown: a CSV past this is cut (the rest downloads). */
export const CSV_ROWS = 1000;

/** RFC 4180: quoted fields may hold the separator, newlines and "" for a quote. */
export function parseCSV(text: string, sep = ','): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length && rows.length < CSV_ROWS; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') {
        field += '"';
        i++;
      } else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"' && field === '') quoted = true;
    else if (c === sep) {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += c;
  }
  if ((field !== '' || row.length) && rows.length < CSV_ROWS) rows.push([...row, field]);
  return rows;
}
